import Validator from 'validatorjs';
import { isDefined, isTypeOf, TYPE_OBJECT } from './utils';

const forEachField = (state, proc) => {
    Object.keys(state).forEach(key => (key !== 'isValid' && key !== 'isPristine') ? proc(key, state[key]) : null);
};

const noop = () => 0;

const removeEmptyRules = rules => Object.entries(rules)
// eslint-disable-next-line no-unused-vars
    .filter(([_, v]) => v && v.length > 0)
    .reduce((bag, [k, v]) => {
        bag[k] = v;
        return bag;
    }, {});

function getInitialState(fields = [], options = {}) {
    const { initialData = {} } = options;
    const formState = { isValid: false, isPristine: true };
    for (let i = 0; i < fields.length; i += 1) {
        const f = fields[i];
        formState[f] = {
            isValid: false,
            isPristine: true,
            isValidated: false,
            value: initialData[f] || '',
            validation: {},
        };
    }
    return formState;
}

const DEF_STATE_FIELD_NAME = 'formState';

export default class Handler {
    static getData(state) {
        const formData = {};
        forEachField(state, (field, stateFieldValue) => {
            formData[field] = stateFieldValue.value;
        });
        return formData;
    }

    /**
     * @param validationMessages: Custom validation messages.
     * @param rules(required): The validation rules, to know more visit https://github.com/skaterdav85/validatorjs
     * @param stateUpdater(optional): function that will update the state of the react component with the form state,
     * usually is `this.setState.bind(this)`.
     * @param formStateName(optional): String with the field name in the state where the form state will live, default is
     * `formState`
     * @param initialData(optional): Initial form data
     */
    constructor(rules, {
        validationMessages, stateUpdater, formStateName, initialData,
    } = {}) {
        if (!rules) {
            throw Error('The rules are required.');
        }
        this._fields = Object.keys(rules);
        this._validationRules = removeEmptyRules(rules);
        this._validationMessages = validationMessages;
        this._stateUpdater = stateUpdater || noop;
        this._stateFieldName = formStateName || DEF_STATE_FIELD_NAME;
        this._prepareState(initialData);
    }

    _prepareState(data) {
        this._formState = getInitialState(this._fields, {initialData: data});
        this._initialFormState = this._formState;
        this._invalidations = {};
    }

    getStateFieldName() {
        return this._stateFieldName;
    }

    resetForm(data) {
        if (data) {
            this._formState = getInitialState(this._fields, {initialData: data});
            this._initialFormState = this._formState;
            this._invalidations = {};
        } else {
            this._formState = this._initialFormState;
        }
        return this;
    }

    clearForm() {
        this._formState = getInitialState(this._fields, {});
        return this;
    }

    getFormState() {
        return this._formState;
    }

    handleFormChange(field, value) {
        if (typeof field === 'string') {
            this._fieldChange({ [field]: value });
        } else if (isTypeOf(field, TYPE_OBJECT)) {
            this._fieldChange(field);
        } else {
            throw new TypeError(`The field argument must be a string or an object but ${Object.prototype.toString.call(field)} was founded.`);
        }
        return this;
    }

    _fieldChange(fields) {
        const changes = {};
        let existChanges = false;
        forEachField(fields, (prop, value) => {
            if (isDefined(this._formState[prop]) && this._formState[prop].value !== value) {
                existChanges = true;
                changes[prop] = { ...this._formState[prop], value, isPristine: false };
            }
        });
        if (existChanges) {
            this._formState = { ...this._formState, ...changes, isPristine: false };
        }
    }

    invalidateField(field, value, msg) {
        if (value !== undefined) {
            this._invalidations[field] = { value, msg };
        }
        return this;
    }

    validateForm() {
        const validation = new Validator(Handler.getData(this._formState), this._validationRules, this._validationMessages);
        let isValid = validation.passes();
        const { errors = {} } = validation;
        const validationResult = {};
        forEachField(this._validationRules, (fieldName) => {
            const { isPristine } = this._formState[fieldName];
            validationResult[fieldName] = {
                isValid: isPristine ? true : !errors.first(fieldName),
                isValidated: !isPristine,
                isPristine,
                value: this._formState[fieldName].value,
                primaryMessage: isPristine ? undefined : errors.first(fieldName),
            };

            // If the field is already invalid we don't check invalidation's
            if (validationResult[fieldName].isValid) {
                const { value, msg } = this._invalidations[fieldName] || { value: null };
                if (this._formState[fieldName].value === value) {
                    isValid = false;
                    validationResult[fieldName] = {
                        ...validationResult[fieldName], isValid: false, isValidated: true, primaryMessage: msg,
                    };
                }
            }
        });
        this._formState = { ...this._formState, ...validationResult, isValid };
        return this;
    }

    updateState(extras = {}) {
        this._stateUpdater({ [this._stateFieldName]: this.getFormState(), ...extras });
        return this;
    }
}
